import type { FieldValues, Path } from 'react-hook-form'

import { type CommonFieldProps, useCheckbox } from '@subaio/components/src/HookForm'

import { ChangedIndicator, Label, RowLabel } from '../Shared'
import { CheckboxInput } from './CheckboxInput'
import { CheckIcon } from './CheckIcon'
import { CheckMark } from './CheckMark'
import { FieldWrapper } from './FieldWrapper'
import { HintContainer } from './HintContainer'
import { InputError } from './InputError'

export const Checkbox = <TFieldValues extends FieldValues, TPath extends Path<TFieldValues>>({
	name,
	label,
	hint,
	isDisabled = false,
	isReadOnly = false,
	isFullWidth = true,
	isRequired = false,
	showChanged,
	testId,
	...rest
}: CommonFieldProps<TPath>) => {
	const {
		id,
		isChecked,
		initialIsChecked,
		isFocusVisible,
		isHovered,
		focusProps,
		hoverProps,
		hasError,
		error,
		showChangedFields,
		inputProps,
	} = useCheckbox<TFieldValues, TPath>({
		name,
		isDisabled,
		isReadOnly,
		isRequired,
		showChanged,
	})

	return (
		<>
			<FieldWrapper isFullWidth={isFullWidth}>
				<RowLabel {...focusProps} {...hoverProps} data-test-id={testId} {...rest}>
					<CheckMark
						isChecked={isChecked}
						isDisabled={isDisabled}
						isFocusVisible={isFocusVisible}
						isHovered={isHovered}
						isReadOnly={isReadOnly}
						isInvalid={hasError}
					>
						{(showChanged ?? showChangedFields) && <ChangedIndicator show={isChecked !== initialIsChecked} />}
						<CheckIcon isChecked={isChecked} />
					</CheckMark>
					<Label htmlFor={id}>
						<span>{label}</span>
						<HintContainer isVisible={Boolean(hint)}>{hint}</HintContainer>
					</Label>
					<CheckboxInput {...inputProps} />
				</RowLabel>
			</FieldWrapper>
			<InputError hasError={hasError} errorMessage={error?.message} hasFocus={isFocusVisible} />
		</>
	)
}
